import {
  collection,
  doc,
  serverTimestamp,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import {
  type FirestoreUserProfileSectionPass,
  type FirestoreUserProfileSectionPassRebate,
  sectionPassSchema,
  sectionPassRebateSchema,
} from './schema'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import {
  collectionSnapshots,
  convertDocumentSnapshotToModel,
  modelItemStream,
} from '../../firestore-mobx/stream'
import { UserProfileSectionPass } from '../../models/UserProfileSectionPass'
import { UserProfileSectionPassRebate } from '../../models/UserProfileSectionPassRebate'
import { addDocWithError, getDocsWithError } from '../../firestore-mobx/fetch'

export * from './schema'

const combinedConverter: FirestoreDataConverter<
  | (FirestoreUserProfileSectionPass & { isRebate: false })
  | (FirestoreUserProfileSectionPassRebate & { isRebate: true })
> = {
  toFirestore: () => {
    throw new Error('unimplemented')
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    const isRebate = 'rebate' in data && data.rebate === true
    if (isRebate) return { ...sectionPassRebateSchema.parse(data), isRebate }
    return { ...sectionPassSchema.parse(data), isRebate }
  },
}

const sectionPassConverter: FirestoreDataConverter<FirestoreUserProfileSectionPass> =
  {
    toFirestore: () => {
      throw new Error('unimplemented')
    },
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return sectionPassSchema.parse(data)
    },
  }

export const getColRef = (firestore: Firestore, userId: string) => {
  return collection(
    firestore,
    'user_profile',
    userId,
    'section_pass'
  ).withConverter(combinedConverter)
}

export const getSectionPassDocRef = (
  firestore: Firestore,
  { userId, sectionId }: { userId: string; sectionId: string }
) => {
  const col = getColRef(firestore, userId)
  return doc(col, sectionId).withConverter(sectionPassConverter)
}

export const getSectionPassForSection = (
  repository: FirebaseRepository,
  { userId, sectionId }: { userId: string; sectionId: string }
) => {
  const docRef = getSectionPassDocRef(repository.firestore, {
    userId,
    sectionId,
  })
  return modelItemStream(repository, docRef, UserProfileSectionPass)
}

export const getSectionPasses = (
  repository: FirebaseRepository,
  { userId }: { userId: string }
) => {
  // filter out rebate section passes from the stream
  return collectionSnapshots(getColRef(repository.firestore, userId), {
    wrapped: true,
  }).map((snapshot) => {
    const models = snapshot.docs
      .filter((doc) => !doc.data().isRebate) // filter out rebates
      .map((doc) => {
        return convertDocumentSnapshotToModel(
          repository,
          doc as QueryDocumentSnapshot<FirestoreUserProfileSectionPass>,
          UserProfileSectionPass
        )
      })
    return models
  })
}

export const getSectionPassesAndRebates = (
  repository: FirebaseRepository,
  { userId }: { userId: string }
) => {
  // filter out rebate section passes from the stream
  return collectionSnapshots(getColRef(repository.firestore, userId), {
    wrapped: true,
  }).map((snapshot) => {
    const models = snapshot.docs.reduce(
      (acc, doc) => {
        const { isRebate } = doc.data()
        if (isRebate)
          acc.rebates.push(
            convertDocumentSnapshotToModel(
              repository,
              doc as QueryDocumentSnapshot<FirestoreUserProfileSectionPassRebate>,
              UserProfileSectionPassRebate
            )
          )
        else
          acc.passes.push(
            convertDocumentSnapshotToModel(
              repository,
              doc as QueryDocumentSnapshot<FirestoreUserProfileSectionPass>,
              UserProfileSectionPass
            )
          )
        return acc
      },
      {
        passes: [] as UserProfileSectionPass[],
        rebates: [] as UserProfileSectionPassRebate[],
      }
    )
    return models
  })
}

export const fetchSectionPassesForUser = async (
  repository: FirebaseRepository,
  { userId }: { userId: string }
): Promise<UserProfileSectionPass[]> => {
  // Use the combined converter to handle both passes and rebates, then filter
  const ref = getColRef(repository.firestore, userId)
  const snapshot = await getDocsWithError(ref, 'FetchSectionPassesForUserError')

  // Filter out rebates - they have isRebate set to true
  return snapshot.docs
    .filter((doc) => !doc.data().isRebate)
    .map((doc) =>
      convertDocumentSnapshotToModel(
        repository,
        doc as QueryDocumentSnapshot<FirestoreUserProfileSectionPass>,
        UserProfileSectionPass
      )
    )
}

export type SectionPassesAndRebates = {
  passes: UserProfileSectionPass[]
  rebates: UserProfileSectionPassRebate[]
}

export const fetchSectionPassesAndRebatesForUser = async (
  repository: FirebaseRepository,
  { userId }: { userId: string }
): Promise<SectionPassesAndRebates> => {
  const ref = getColRef(repository.firestore, userId)
  const snapshot = await getDocsWithError(
    ref,
    'FetchSectionPassesAndRebatesForUserError'
  )

  return snapshot.docs.reduce(
    (acc, doc) => {
      const { isRebate } = doc.data()
      if (isRebate)
        acc.rebates.push(
          convertDocumentSnapshotToModel(
            repository,
            doc as QueryDocumentSnapshot<FirestoreUserProfileSectionPassRebate>,
            UserProfileSectionPassRebate
          )
        )
      else
        acc.passes.push(
          convertDocumentSnapshotToModel(
            repository,
            doc as QueryDocumentSnapshot<FirestoreUserProfileSectionPass>,
            UserProfileSectionPass
          )
        )
      return acc
    },
    {
      passes: [] as UserProfileSectionPass[],
      rebates: [] as UserProfileSectionPassRebate[],
    }
  )
}

export const createSectionPassRebate = async (
  repository: FirebaseRepository,
  {
    userId,
    price,
  }: {
    userId: string
    price: number
  }
) => {
  const collection = getColRef(repository.firestore, userId).withConverter(null)

  await addDocWithError(collection, {
    consumedAt: null,
    createdAt: serverTimestamp(),
    paymentId: null,
    price,
    purchaseDate: serverTimestamp(),
    purchaseType: 'grant',
    rebate: true,
    updatedAt: serverTimestamp(),
    userId,
  })
}
